/** Pure grouping for protection-coordination pairs (UX Stage 1, C2 / spec
 *  4.Stage1.12).
 *
 *  Pairs the engine could not evaluate carry
 *  `coordination_basis === 'cannot_assess'` — non-fuse pairs, missing curves,
 *  unplaced devices (services/fault_analysis.py::_evaluate_pair) — and state
 *  the blocking reason in `notes`. Instead of interleaving them with evaluated
 *  rows, the UI renders them grouped by that reason. Pairs with basis
 *  'tcc_curve' — or a null/absent basis (legacy responses predating the
 *  Phase-3 protection layer) — stay individual table rows; the service always
 *  stamps a basis on cannot-assess pairs, so null never means "blocked". */

import type { ProtectionCoordinationPair } from '../../types/faultAnalysis.ts'

export interface BlockedPairGroup {
  reason: string
  pairs: ProtectionCoordinationPair[]
}

export const NO_REASON_LABEL = 'No blocking reason recorded'

export function splitPairsByBasis(pairs: ProtectionCoordinationPair[]): {
  evaluated: ProtectionCoordinationPair[]
  blocked: ProtectionCoordinationPair[]
} {
  const evaluated: ProtectionCoordinationPair[] = []
  const blocked: ProtectionCoordinationPair[] = []
  for (const p of pairs) {
    if (p.coordination_basis === 'cannot_assess') blocked.push(p)
    else evaluated.push(p)
  }
  return { evaluated, blocked }
}

/** Group blocked pairs by their blocking reason (`notes`), largest group
 *  first; ties break alphabetically so the order is stable across refetches. */
export function groupBlockedByReason(
  blocked: ProtectionCoordinationPair[],
): BlockedPairGroup[] {
  const byReason = new Map<string, ProtectionCoordinationPair[]>()
  for (const p of blocked) {
    const reason = p.notes || NO_REASON_LABEL
    const group = byReason.get(reason)
    if (group) group.push(p)
    else byReason.set(reason, [p])
  }
  return [...byReason.entries()]
    .map(([reason, pairs]) => ({ reason, pairs }))
    .sort(
      (a, b) => b.pairs.length - a.pairs.length || a.reason.localeCompare(b.reason),
    )
}
